import { type FC, useState, useEffect } from 'react';
import Button from '2-core-elements/Buttons/Button';
import Icon from '2-core-elements/Icon/Icon';

interface SearchPaginationProps {
    currentPage: number;
    totalItems: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
}

const SearchPagination: FC<SearchPaginationProps> = ({ currentPage, totalItems, itemsPerPage, onPageChange }: SearchPaginationProps) => {
    const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

    useEffect(() => {
        const handleResize = (): void => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const getVisiblePageNumbers = (): (number | string)[] => {
        const totalPageButtons = windowWidth < 768 ? 3 : 5;
        const pageNumbers = [];

        if (totalPages <= totalPageButtons) {
            return Array.from({ length: totalPages }, (_, i) => {
                return i + 1;
            });
        }

        pageNumbers.push(1);

        const start = Math.max(2, currentPage - Math.floor((totalPageButtons - 2) / 2));
        const end = Math.min(totalPages - 1, start + totalPageButtons - 3);

        if (start > 2) {
            pageNumbers.push('ellipsis');
        }

        for (let i = start; i <= end; i += 1) {
            pageNumbers.push(i);
        }

        if (end < totalPages - 1) {
            pageNumbers.push('ellipsis');
        }

        if (totalPages > 1) {
            pageNumbers.push(totalPages);
        }

        return pageNumbers;
    };

    const handleNextPage = (): void => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    const handlePrevPage = (): void => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    return (
        <div className="pagination-controls">
            <Button
                onlyIcon={true}
                skin={'3'}
                prefixIcon={'chevron-left'}
                onClick={handlePrevPage}
                disabled={currentPage === 1}
            />
            {getVisiblePageNumbers().map((pageNumber, index) => {
                if (pageNumber === 'ellipsis') {
                    const ellipsisKey = `ellipsis-${index}`;
                    return <Icon icon={'more-horiz'} key={ellipsisKey} className="pagination-ellipsis" />;
                }

                return (
                    <Button
                        key={pageNumber}
                        size={'sm'}
                        onClick={(): void => {
                            onPageChange(pageNumber as number);
                        }}
                        className={`pagination-button ${currentPage === pageNumber ? 'pagination-selected' : ''}`}
                    >
                        {pageNumber}
                    </Button>
                );
            })}
            <Button
                onlyIcon={true}
                skin={'3'}
                prefixIcon={'chevron-right'}
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
            />
        </div>
    );
};

export default SearchPagination;
